import { useEffect, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Modal,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { useShipperShipmentPageContext, useWriterComponentState } from "hooks";
import { MODAL_STYLE } from "layouts/constants";

import { ClientAppEvent } from "../../../../../../constants";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onNoteCreated: () => void;
}

const MAX_NOTE_LENGTH = 1000;

export function CreateShipmentNoteModal(props: Props) {
  const { isOpen, onClose, onNoteCreated } = props;
  const { shipment } = useShipperShipmentPageContext();
  const posthog = usePostHog();
  const [text, setText] = useState<string>("");

  const { loading, error, write, success, resetState } =
    useWriterComponentState({
      errorMessage: "Failed to create note. Please try again.",
      writeConfig: {
        method: "post",
        url: "/shipper-shipment-notes",
        body: { shipment_id: shipment?.info?.id, text },
      },
      errorMessageKey: "message",
    });

  useEffect(() => {
    if (success && isOpen) {
      setText("");
      resetState();
      onNoteCreated();
    }
  }, [success, isOpen, resetState, onNoteCreated]);

  const handleClose = () => {
    setText("");
    resetState();
    onClose();
  };

  const isValidNote = text.trim().length > 0 && text.length <= MAX_NOTE_LENGTH;

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Box sx={MODAL_STYLE}>
        <Stack justifyContent="flex-end" direction="row">
          <Tooltip title="Close without saving the note">
            <IconButton aria-label="Close" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Stack>
        <Stack direction="column" spacing={2}>
          <Typography variant="h4" component="h2" sx={{ mb: "1rem" }}>
            Add Private Note
          </Typography>
          <Alert severity="info" sx={{ mt: 1 }}>
            This note will be visible to you and any teammate(s) you have shared
            this shipment with. Carriers cannot view or access these notes.
          </Alert>

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Note"
            placeholder="Enter your note here..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            inputProps={{ maxLength: MAX_NOTE_LENGTH }}
            helperText={`${text.length}/${MAX_NOTE_LENGTH} characters`}
          />

          <ErrorTypography error={error} />

          {loading ? (
            <Stack alignItems="center">
              <CircularProgress />
            </Stack>
          ) : (
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="text" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                disabled={!isValidNote}
                variant="contained"
                onClick={() =>
                  write(() => {
                    posthog?.capture(
                      ClientAppEvent.SHIPMENT_PRIVATE_NOTE_CREATED,
                      {
                        source: "shipment-detail",
                      },
                    );
                  })
                }
              >
                Save Note
              </Button>
            </Stack>
          )}
        </Stack>
      </Box>
    </Modal>
  );
}
